import { multiUploadTask, newMultiParts, newUploadTask } from '@/api/upload.js';
import message from '@/utils/message.jsx';
import SentryTracker from '@/utils/Sentry/sentryUtils.js';
import i18n from './i18n';
const FILE_SLICE_SIZE = 5 * 1024 * 1024;
const PARALLEL_COUNT = 5;
const MAX_RETRY_COUNT = 3;
const RETRY_INTERVAL = [1000, 2000, 4000];

function sliceFile(file) {
  const chunks = [];
  let pointer = 0;
  while (pointer < file.size) {
    chunks.push({
      url: '',
      eTag: '',
      blob: file.slice(pointer, (pointer += FILE_SLICE_SIZE)),
    });
  }
  return chunks;
}

function putChunk(url, chunk) {
  SentryTracker.captureDebug('putChunk:', url, chunk.size);
  return fetch(url, {
    method: 'PUT',
    body: chunk,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  }).catch((e) => {
    SentryTracker.captureDebug('putChunk error:', url);
    SentryTracker.captureError(e);
    throw e;
  });
}

function sleep(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

async function flushChunks(chunks, signal, progressCb, showMessage) {
  let pointer = 0;
  let allSuccess = true;
  while (pointer < chunks.length) {
    SentryTracker.captureDebug('upload chunks from:', pointer);
    const parallelChunks = chunks.slice(pointer, (pointer += PARALLEL_COUNT));
    const putResult = await Promise.allSettled(
      parallelChunks.map(async ({ url, blob, uploadId, partNumber }) => {
        if (!url) {
          const res = await newMultiParts({
            uploadId,
            parts: 1,
            startPartNumber: partNumber,
          });
          if (res?.code !== 0) {
            return Promise.reject(new Error('Failed to get part url'));
          }
          url = res.data.urls[0];
        }
        return putChunk(url, blob);
      }),
    );
    putResult.forEach(({ status, value }, index) => {
      if (status === 'fulfilled') {
        parallelChunks[index].eTag = value.headers.get('ETag');
        parallelChunks[index].blob = null;
        parallelChunks[index].url = null;
      } else {
        allSuccess = false;
        if (showMessage) {
          message.info(i18n.t('As_Error_Network'), {
            id: 'uploadFailed',
          });
        }
      }
    });
    SentryTracker.captureDebug('upload chunks end:', pointer, ',  allSuccess:', allSuccess);
    if (signal.aborted) {
      allSuccess = false;
      break;
    }
    progressCb && progressCb();
    await sleep(5);
  }
  return allSuccess;
}

const isChunkFailed = (chunk) => !chunk.eTag && chunk.blob;

function buildUsage(usageType, params) {
  let usage = { type: usageType };
  if (usageType === 0) {
    //project
  } else if (usageType === 1) {
    // thumbnail
    // do nothing
  } else if (usageType === 2) {
    // avatar
    usage = { ...usage, ext: params['ext'] };
  } else if (usageType === 3) {
    // voice_clone
    // do nothing
  } else if (usageType === 4) {
    // asset
    usage = { ...usage, filename: params['filename'] };
  }
  return usage;
}

export default class Uploader {
  #usage;
  #file;
  #chunks = [];
  #uploadId;
  #retryTimeout = [];
  #controller = new AbortController();
  #progressCb;
  #showMessage = false;

  constructor(usage, progressCb, params) {
    this.#usage = buildUsage(usage, params);
    this.#progressCb = progressCb;
    this.#showMessage = params?.showMessage || false;
  }

  uploadFile(file) {
    this.#file = file;
    this.#chunks = [
      {
        url: '',
        eTag: '',
        blob: file,
      },
    ];
    return this.#uploadChunks();
  }

  uploadFileWithSlice(file) {
    this.#file = file;
    this.#chunks = sliceFile(file);
    return this.#uploadChunks();
  }

  uploadVideoProject(file) {
    this.#file = file;
    this.#chunks = sliceFile(file);
    return this.#uploadVideoProjectChunks();
  }

  #uploadVideoProjectChunks() {
    return new Promise((resolve, reject) => {
      this.#controller.signal.addEventListener(
        'abort',
        () => {
          reject(new Error('Manual Stop'));
        },
        {
          once: true,
        },
      );
      multiUploadTask({
        streams: [
          {
            ext: 'mp4',
            mimeType: 'video/mp4',
          },
        ],
        parts: this.#chunks.length,
        thumbnail: true,
      }).then((res) => {
        if (res?.code !== 0) {
          console.error('newUploadTask failed');
          reject();
        } else {
          const { uploadIds, contentUrls } = res.data;
          this.#uploadId = uploadIds[0];
          contentUrls.forEach((url, index) => {
            this.#chunks[index].url = url;
            this.#chunks[index].partNumber = index + 1;
          });

          this.#decorateFlush(this.#chunks).then((success) => {
            if (!success && !this.#controller.signal.aborted) {
              this.#retryUpload(this.#chunks);
            }
            resolve({
              uploadId: uploadIds[0],
              eTags: this.#chunks.map((chunk) => ({
                partNumber: chunk.partNumber,
                eTag: chunk.eTag,
              })),
              thumbnailUploadId: uploadIds[1],
            });
          });
        }
      });
    });
  }

  #decorateFlush(chunks) {
    return flushChunks(
      chunks,
      this.#controller.signal,
      () => {
        const total = this.#chunks.length;
        const failed = this.#chunks.filter(isChunkFailed).length;
        const percent = (total - failed) / total;
        this.#progressCb && this.#progressCb(percent);
      },
      this.#showMessage,
    );
  }

  #uploadChunks() {
    return new Promise((resolve, reject) => {
      this.#controller.signal.addEventListener(
        'abort',
        () => {
          reject(new Error('Manual Stop'));
        },
        {
          once: true,
        },
      );
      newUploadTask({
        usage: this.#usage,
        fileExtension: this.#file.name.replace(/.*\./g, ''),
        assetSize: this.#file.size,
        parts: this.#chunks.length,
      }).then((res) => {
        if (res?.code !== 0) {
          console.error('newUploadTask failed');
          reject(res);
        } else {
          const { uploadId, urls } = res.data;
          this.#uploadId = uploadId;
          urls.forEach((url, index) => {
            this.#chunks[index].url = url;
            this.#chunks[index].partNumber = index + 1;
          });

          this.#decorateFlush(this.#chunks).then((success) => {
            if (!success && !this.#controller.signal.aborted) {
              this.#retryUpload(this.#chunks);
            }
            resolve({
              uploadId,
              eTags: this.#chunks.map((chunk) => ({
                partNumber: chunk.partNumber,
                eTag: chunk.eTag,
              })),
            });
          });
        }
      });
    });
  }

  async #retryUpload(chunks) {
    let success = true;
    let retryCount = 0;
    while (retryCount < MAX_RETRY_COUNT) {
      if (this.#controller.signal.aborted) {
        break;
      }
      const failedChunks = chunks.filter(isChunkFailed);
      if (failedChunks.length === 0) break;
      success = await new Promise((resolve) => {
        this.#retryTimeout.push(
          setTimeout(async () => {
            resolve(this.#decorateFlush(failedChunks));
          }, RETRY_INTERVAL[retryCount]),
        );
      });
      if (success) break;
      retryCount++;
    }
    if (!success) {
      console.error(`Retry Failed`);
    }
  }

  setUploadId(id) {
    this.#uploadId = id;
  }

  async uploadBlobSlice(blob) {
    const partNumber = this.#chunks.length + 1;
    const chunk = {
      uploadId: this.#uploadId,
      url: null,
      eTag: '',
      blob,
      partNumber,
    };
    this.#chunks.push(chunk);
    const success = await this.#decorateFlush([chunk]);
    if (!success && !this.#controller.signal.aborted) {
      this.#retryUpload([chunk]);
    }
  }

  async complete() {
    let success = true;
    const failedChunks = this.#chunks.filter(isChunkFailed);
    if (failedChunks.length > 0) {
      if (this.#retryTimeout.length > 0) {
        this.#retryTimeout.forEach(clearTimeout);
        this.#retryTimeout = [];
      }
      success = await this.#decorateFlush(failedChunks);
    }
    if (!success) {
      throw new Error('Complete Failed');
    }
    return {
      uploadId: this.#uploadId,
      eTags: this.#chunks.map((chunk) => ({
        partNumber: chunk.partNumber,
        eTag: chunk.eTag,
      })),
    };
  }

  stop() {
    this.#controller.abort('Manual Stop');
  }
}
